import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { ApiError, api } from '@/lib/api';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { WikiCategory, WikiCategoryList } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GripVertical, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

interface DraftRow {
  id: string;
  name: string;
  description: string;
  uploadOpenToUsers: boolean;
  color: string;
}

const QUERY_KEY = ['admin', 'wiki', 'categories'] as const;

export function AdminWikiCategories() {
  const queryClient = useQueryClient();
  const listQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: api.adminWikiCategoriesList,
  });

  const [drafts, setDrafts] = useState<Record<string, DraftRow>>({});
  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newName, setNewName] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function getDraft(c: WikiCategory): DraftRow {
    return (
      drafts[c.id] ?? {
        id: c.id,
        name: c.name,
        description: c.description ?? '',
        uploadOpenToUsers: c.uploadOpenToUsers,
        color: c.color,
      }
    );
  }

  const updateMutation = useMutation({
    mutationFn: (args: DraftRow) =>
      api.adminWikiCategoryUpdate(args.id, {
        name: args.name,
        description: args.description.length > 0 ? args.description : null,
        uploadOpenToUsers: args.uploadOpenToUsers,
        color: args.color,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['wiki', 'categories'] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  const createMutation = useMutation({
    mutationFn: (args: { slug: string; name: string; uploadOpenToUsers: boolean }) =>
      api.adminWikiCategoryCreate({
        slug: args.slug,
        name: args.name,
        description: null,
        uploadOpenToUsers: args.uploadOpenToUsers,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['wiki', 'categories'] });
      setCreating(false);
      setNewSlug('');
      setNewName('');
      setNewOpen(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.adminWikiCategoryDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['wiki', 'categories'] });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) => api.adminWikiCategoriesReorder(orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['wiki', 'categories'] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const current = listQuery.data?.categories ?? [];
    const oldIndex = current.findIndex((c) => c.id === active.id);
    const newIndex = current.findIndex((c) => c.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(current, oldIndex, newIndex);
    queryClient.setQueryData<WikiCategoryList>(QUERY_KEY, { categories: reordered });
    setError(null);
    reorderMutation.mutate(reordered.map((c) => c.id));
  }

  function onCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    createMutation.mutate({
      slug: newSlug.trim(),
      name: newName.trim(),
      uploadOpenToUsers: newOpen,
    });
  }

  const categories = listQuery.data?.categories ?? [];

  return (
    <PageShell
      header={
        <PageHeader
          title="위키 카테고리"
          description="전사 공지/인사/총무 등 분류. 좌측 핸들을 잡고 드래그해서 순서를 바꿉니다."
          actions={
            <button
              type="button"
              onClick={() => setCreating((v) => !v)}
              className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm font-medium text-accent-foreground hover:opacity-90"
            >
              <Plus className="h-4 w-4" />새 카테고리
            </button>
          }
        />
      }
    >
      <div className="space-y-6">
        {error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {creating ? (
          <form
            onSubmit={onCreate}
            className="rounded-md border border-border bg-surface p-4 space-y-3"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium" htmlFor="new-cat-slug">
                  slug (소문자/숫자/하이픈)
                </label>
                <input
                  id="new-cat-slug"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.currentTarget.value)}
                  placeholder="hr-leave"
                  className="w-full rounded border border-border bg-background px-3 py-1.5 text-sm focus:border-accent focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium" htmlFor="new-cat-name">
                  표시명
                </label>
                <input
                  id="new-cat-name"
                  value={newName}
                  onChange={(e) => setNewName(e.currentTarget.value)}
                  placeholder="휴가 정책"
                  className="w-full rounded border border-border bg-background px-3 py-1.5 text-sm focus:border-accent focus:outline-none"
                  required
                />
              </div>
              <label className="flex items-center gap-2 self-end text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={newOpen}
                  onChange={(e) => setNewOpen(e.currentTarget.checked)}
                />
                일반 사용자도 업로드 가능
              </label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-accent-foreground"
              >
                {createMutation.isPending ? '생성 중…' : '생성'}
              </button>
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="rounded border border-border px-3 py-1.5 text-sm"
              >
                취소
              </button>
            </div>
            <p className="text-xs text-foreground-faint">
              새 카테고리는 목록 맨 아래에 추가됩니다. 드래그로 위치를 옮길 수 있습니다.
            </p>
          </form>
        ) : null}

        <div className="overflow-hidden rounded-md border border-border">
          <div className="grid grid-cols-[28px_140px_minmax(160px,1fr)_minmax(200px,2fr)_110px_60px_60px_210px] items-center gap-2 border-b border-border bg-surface px-3 py-2 text-xs font-medium text-foreground-muted">
            <span />
            <span>slug</span>
            <span>이름</span>
            <span>설명</span>
            <span className="text-center">사용자 업로드</span>
            <span className="text-center">페이지</span>
            <span className="text-center">색상</span>
            <span className="text-right">작업</span>
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={categories.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {categories.map((c) => (
                <CategoryRow
                  key={c.id}
                  category={c}
                  draft={getDraft(c)}
                  saving={updateMutation.isPending}
                  onChange={(patch) =>
                    setDrafts((prev) => ({
                      ...prev,
                      [c.id]: { ...getDraft(c), ...patch },
                    }))
                  }
                  onSave={() => updateMutation.mutate(getDraft(c))}
                  onDelete={() => {
                    if (
                      window.confirm(
                        `카테고리 "${c.name}" 를 삭제합니다. 하위 페이지의 카테고리는 분류되지 않음으로 변경됩니다. 계속?`,
                      )
                    )
                      deleteMutation.mutate(c.id);
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>

          {categories.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-foreground-faint">
              아직 카테고리가 없습니다. 우상단 "새 카테고리" 로 추가하세요.
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
}

interface CategoryRowProps {
  category: WikiCategory;
  draft: DraftRow;
  saving: boolean;
  onChange: (patch: Partial<DraftRow>) => void;
  onSave: () => void;
  onDelete: () => void;
}

function CategoryRow({ category, draft, saving, onChange, onSave, onDelete }: CategoryRowProps) {
  const queryClient = useQueryClient();
  const rerunMutation = useMutation({
    mutationFn: () => api.adminWikiHealthCheckRunCategory(category.slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'health-runs'] });
      toast.success(`'${category.name}' 카테고리 재분석을 시작했습니다`, {
        description: '진행/결과는 위키 건강검진 화면에서 확인할 수 있습니다.',
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : '재실행 큐잉 실패');
    },
  });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-[28px_140px_minmax(160px,1fr)_minmax(200px,2fr)_110px_60px_60px_210px] items-center gap-2 border-b border-border-muted bg-background px-3 py-2 last:border-b-0"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="순서 변경"
        className="flex h-7 w-7 cursor-grab items-center justify-center rounded text-foreground-faint hover:bg-surface hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="truncate font-mono text-xs text-foreground-faint" title={category.slug}>
        {category.slug}
      </span>
      <input
        value={draft.name}
        onChange={(e) => onChange({ name: e.currentTarget.value })}
        className="w-full rounded border border-border bg-background px-2 py-1 text-sm focus:border-accent focus:outline-none"
      />
      <input
        value={draft.description}
        onChange={(e) => onChange({ description: e.currentTarget.value })}
        placeholder="(없음)"
        className="w-full rounded border border-border bg-background px-2 py-1 text-sm focus:border-accent focus:outline-none"
      />
      <div className="text-center">
        <input
          type="checkbox"
          checked={draft.uploadOpenToUsers}
          onChange={(e) => onChange({ uploadOpenToUsers: e.currentTarget.checked })}
        />
      </div>
      <span className="text-center text-foreground-muted">{category.pageCount ?? 0}</span>
      <div className="flex items-center justify-center">
        <input
          type="color"
          value={draft.color}
          onChange={(e) => onChange({ color: e.currentTarget.value })}
          className="h-7 w-10 cursor-pointer rounded border border-border bg-background"
          title="지식 그래프 색상"
        />
      </div>
      <div className="flex items-center justify-end gap-1 whitespace-nowrap">
        <button
          type="button"
          onClick={() => {
            if (
              window.confirm(
                `'${category.name}' 카테고리(${category.pageCount ?? 0}페이지) 만 LLM 으로 재분석(건강검진)합니다. 다른 카테고리는 변경하지 않습니다. 토큰 비용이 발생합니다. 계속?`,
              )
            ) {
              rerunMutation.mutate();
            }
          }}
          disabled={rerunMutation.isPending || (category.pageCount ?? 0) === 0}
          className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-accent disabled:opacity-50"
          title="이 카테고리만 재분석(건강검진) 실행"
        >
          <RotateCcw className={`h-3.5 w-3.5 ${rerunMutation.isPending ? 'animate-spin' : ''}`} />
          {rerunMutation.isPending ? '…' : '재실행'}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded bg-accent px-2 py-1 text-xs text-accent-foreground disabled:opacity-50"
        >
          저장
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded border border-destructive/40 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
          title="삭제"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
